import { useHover } from '@react-aria/interactions';
import { Button } from '@shared/components';
import { ActionTimelineEvent } from '@shared/components/events-timeline/components/action-timeline-event';
import { FlowIcon } from '@shared/components/flow-icon/FlowIcon';
import { InlineDateField } from '@shared/components/inline-date-field/InlineDateField';
import { InlineTimeField } from '@shared/components/inline-time-field/InlineTimeField';
import { useQuoteFormAdaptorContext } from '@shared/contexts/QuoteFormAdaptorContext';
import { getElementSeparator } from '@shared/plugin/utils/intersperseElements';

import { DistanceDisplay } from '@shared/features/quote-details/DistanceDisplay';
import {
  getIsRequiredField,
  getValidateAddress,
} from '@shared/utils/forms/formAdaptor';
import { QuoteFormValues, Stop } from '@shared/utils/forms/types';
import { StopType } from 'clerk_common/types/stop';
import { isNil } from 'lodash';
import { Trash2 } from 'react-feather';
import { Path, useFormContext } from 'react-hook-form';
import { AddressSearch } from './AddressSearch';

const getDisplayStopType = (stopType: StopType, i: number, length: number) => {
  if (stopType === 'DELIVERY' && i === length - 1) {
    return 'DELIVERY';
  }

  return 'PICKUP';
};

type RouteStopProps = {
  index: number;
  stopType: StopType;
  displayStopType: 'PICKUP' | 'DELIVERY';
  removeStop: () => void;
  el: (strings: TemplateStringsArray) => string;
  allowCustomAddress?: boolean;
  mileage?: number;
};

const RouteStop = (p: RouteStopProps) => {
  const { index, stopType, displayStopType, el } = p;

  const { save, integrationConfigs } = useQuoteFormAdaptorContext();
  const { watch } = useFormContext<QuoteFormValues>();

  const { isHovered, hoverProps } = useHover({});

  const stopPrefix =
    stopType === 'PICKUP' ? 'pickup' : (`stops.${index}` as const);

  const dateName: Path<QuoteFormValues> = `${stopPrefix}.date`;
  const isDateRequired = getIsRequiredField(dateName, integrationConfigs);

  const locationName: Path<QuoteFormValues> = `${stopPrefix}.location`;

  const timeRange = watch(`${stopPrefix}.timeRange` as Path<QuoteFormValues>);
  const hasTimeRange = !isNil(timeRange);

  const earlyTimeName: Path<QuoteFormValues> = `${stopPrefix}.timeRange.startTimeOrOnlyTime`;
  const lateTimeName: Path<QuoteFormValues> = `${stopPrefix}.timeRange.optionalEndTime`;
  const singleTimeName: Path<QuoteFormValues> = `${stopPrefix}.time`;

  const isEarlyTimeRequired = hasTimeRange
    ? getIsRequiredField(earlyTimeName, integrationConfigs)
    : false;
  const isLateTimeRequired = hasTimeRange
    ? getIsRequiredField(lateTimeName, integrationConfigs)
    : false;
  const isSingleTimeRequired = !hasTimeRange
    ? getIsRequiredField(singleTimeName, integrationConfigs)
    : false;

  return (
    <div {...hoverProps}>
      <ActionTimelineEvent type={displayStopType}>
        <div className={el`route-step`}>
          {p.mileage ? (
            <DistanceDisplay
              multiStopMileage={{
                value: p.mileage,
                units: 'MI',
              }}
            />
          ) : null}
          <div className={el`datetime-row`}>
            <div className={el`datetime-wrapper`}>
              <InlineDateField<QuoteFormValues>
                save={save}
                name={dateName}
                required={isDateRequired}
              />
              <FlowIcon fieldPath={dateName} type="DATE" />
              {getElementSeparator({ className: el`separator` })}
              {hasTimeRange ? (
                <>
                  <InlineTimeField<QuoteFormValues>
                    save={save}
                    name={earlyTimeName}
                    required={isEarlyTimeRequired}
                    placeholder="Early time"
                  />
                  <FlowIcon fieldPath={earlyTimeName} />
                  {getElementSeparator({ className: el`separator` })}
                  <InlineTimeField<QuoteFormValues>
                    save={save}
                    name={lateTimeName}
                    required={isLateTimeRequired}
                    placeholder="Late time"
                  />
                  <FlowIcon fieldPath={lateTimeName} />
                </>
              ) : (
                <>
                  <InlineTimeField<QuoteFormValues>
                    save={save}
                    name={singleTimeName}
                    required={isSingleTimeRequired}
                  />
                  <FlowIcon fieldPath={singleTimeName} />
                </>
              )}
            </div>
            {stopType == 'DELIVERY' && isHovered && (
              <Button
                isGhost
                size="xs"
                tooltip="Remove stop"
                icon={<Trash2 className="text-gray-400" size={12} />}
                onPress={p.removeStop}
              />
            )}
          </div>
          <AddressSearch<QuoteFormValues>
            save={save}
            name={locationName}
            required
            validateAddress={getValidateAddress(
              `${locationName}.address`,
              integrationConfigs
            )}
            allowCustomAddress={p.allowCustomAddress}
          />
        </div>
      </ActionTimelineEvent>
    </div>
  );
};

interface RouteSegmentProps {
  stops: Stop[];
  stopType: StopType;
  mileageBetweenStops?: number[];
  removeStop: (idx: number) => void;
  el: (strings: TemplateStringsArray) => string;
  allowCustomAddress?: boolean;
}

export const RouteSegment = ({
  stops,
  stopType,
  mileageBetweenStops,
  removeStop,
  el,
  allowCustomAddress,
}: RouteSegmentProps) => {
  return (
    <>
      {stops.map((_, i) => {
        const displayStopType = getDisplayStopType(stopType, i, stops.length);
        const mileage = mileageBetweenStops?.[i] || 0;

        return (
          <RouteStop
            index={i}
            key={`route-segment-${i}`}
            stopType={stopType}
            displayStopType={displayStopType}
            removeStop={() => removeStop(i)}
            el={el}
            allowCustomAddress={allowCustomAddress}
            mileage={mileage}
          />
        );
      })}
    </>
  );
};
